import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import Logo from "@/components/Logo";
import { Button } from "@/components/ui/button";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function BillingSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling"); // polling | paid | failed | expired
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setStatus("failed");
      return;
    }
    let attempts = 0;
    const maxAttempts = 8;
    const poll = async () => {
      attempts += 1;
      try {
        const resp = await fetch(`${BACKEND}/api/checkout/status/${sessionId}`);
        const data = await resp.json();
        setDetail(data);
        if (data.payment_status === "paid") {
          setStatus("paid");
          return;
        }
        if (data.status === "expired") {
          setStatus("expired");
          return;
        }
        if (attempts < maxAttempts) {
          setTimeout(poll, 2000);
        } else {
          setStatus("failed");
        }
      } catch (e) {
        if (attempts < maxAttempts) setTimeout(poll, 2000);
        else setStatus("failed");
      }
    };
    poll();
  }, [sessionId]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-section p-6" data-testid="billing-success-page">
      <div className="w-full max-w-lg rounded-3xl bg-card p-10 text-center shadow-card">
        <Logo size="md" />
        <div className="mt-8">
          {status === "polling" && (
            <>
              <Loader2 className="mx-auto h-12 w-12 animate-spin text-primary" />
              <h1 className="mt-4 font-heading text-2xl font-bold">Confirming your payment…</h1>
              <p className="mt-2 text-sm text-muted-foreground">
                Hang tight — this usually takes a few seconds.
              </p>
            </>
          )}
          {status === "paid" && (
            <>
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-emerald-100">
                <CheckCircle2 className="h-8 w-8 text-emerald-700" />
              </div>
              <h1 className="mt-4 font-heading text-3xl font-extrabold">Welcome to GuardianNest!</h1>
              <p className="mt-2 text-sm text-muted-foreground">
                Payment confirmed. Your plan is now active — {detail?.currency?.toUpperCase() || "USD"}{" "}
                {(detail?.amount_total / 100).toFixed(2)} charged.
              </p>
              <Link to="/dashboard">
                <Button size="lg" className="mt-8" data-testid="billing-goto-dashboard">
                  Go to my dashboard
                </Button>
              </Link>
            </>
          )}
          {status === "expired" && (
            <>
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-secondary">
                <XCircle className="h-8 w-8 text-primary" />
              </div>
              <h1 className="mt-4 font-heading text-2xl font-bold">Session expired</h1>
              <p className="mt-2 text-sm text-muted-foreground">
                No worries — try again anytime.
              </p>
              <Link to="/pricing">
                <Button variant="outline" className="mt-8" data-testid="billing-retry">
                  Back to pricing
                </Button>
              </Link>
            </>
          )}
          {status === "failed" && (
            <>
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-destructive/10">
                <XCircle className="h-8 w-8 text-destructive" />
              </div>
              <h1 className="mt-4 font-heading text-2xl font-bold">Couldn't confirm payment</h1>
              <p className="mt-2 text-sm text-muted-foreground">
                If you were charged, we'll sort it out — reach us on the contact page and we'll
                investigate right away.
              </p>
              <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
                <Link to="/contact">
                  <Button data-testid="billing-contact-support">Contact support</Button>
                </Link>
                <Link to="/">
                  <Button variant="outline" data-testid="billing-home">
                    Back to home
                  </Button>
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
